/**
 * This module is responsible for storing and retrieving the state of the
 * editor in `sessionStorage`, which is scoped to the lifetime of a single
 * browser tab. The tricky bits are:
 *
 *  1. We want to scope the storage by applet id (or new applet), so that we
 *     don't accidentally show one applet's content when we should show a
 *     different one.
 *
 *  2. We want to clear the new applet storage when the user clicks a
 *     "New Applet" button anywhere in the UI, so we have to expose a function
 *     that we can call when that happens.
 *
 *  3. If the user hard-refreshes the page, we should clear the cache.
 */

import throttle from "lodash/throttle"
import { stripExtraFields } from "../reducers/applet"

const saveFrequency = 5000 // ms
const storageId = "composer-v1"

// This is a Redux middleware that just keeps track of the current state of the
// `applet` reducer. Technically we could just do this inside the reducer
// itself, since we only care about one reducer right now, but doing it this
// way lets us keep the saving and loading logic encapsulated and colocated.
export default function storage(store) {
  return next => action => {
    const result = next(action)

    try {
      const state = store.getState()

      if (state.applet && !state.applet.isLoading) {
        persistState(state.applet)
      }
    } catch (e) {
      console.warn("Persisting composer state failed", e)
    }

    return result
  }
}

export function loadFromStorage(appletId) {
  return new Promise((resolve, reject) => {
    let rawData

    try {
      rawData = sessionStorage.getItem(storageId)
    } catch (e) {
      console.warn("Loading state failed:", e)
    }

    if (rawData) {
      const { id, applet } = JSON.parse(rawData)

      if (id === appletId) {
        resolve({ id, ...applet })
      } else {
        reject()
      }
    } else {
      reject()
    }
  })
}

export function clearStorage() {
  try {
    sessionStorage.removeItem(storageId)
  } catch (e) {
    console.warn("Clearing state failed:", e)
  }
}

const persistState = throttle(function (appletState) {
  const [id, applet] = stripExtraFields(appletState)

  try {
    sessionStorage.setItem(storageId, JSON.stringify({ id, applet }))
  } catch (e) {
    console.warn("Setting state failed:", e)
  }
}, saveFrequency)
